// src/pages/Onboarding/slides/ConversionSlide.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import SlideShell from '../SlideShell';
import PrimaryButton from '../../../components/Onboarding/PrimaryButton';
import ResultTimeDisplay from '../../../components/Onboarding/ResultTimeDisplay';
import BenefitsDisplay from '../../../components/Onboarding/BenefitsDisplay';
import BeforeAfterSlider from '../../../components/Onboarding/BeforeAfterSlider';
import { getImageUploadArtifact } from '../../../utils/Onboarding/artifacts';

// Helper to animate values 0 -> target over a duration
function useAnimatedPercent(targetPercent, durationMs = 900, delayMs = 50) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const startAt = performance.now() + delayMs;
    const start = () => {
      const t0 = performance.now();
      const run = (ts) => {
        const elapsed = ts - t0;
        const p = Math.min(1, elapsed / durationMs);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(targetPercent * eased);
        if (p < 1) raf = requestAnimationFrame(run);
      };
      raf = requestAnimationFrame(run);
    };
    const id = setTimeout(start, Math.max(0, delayMs));
    return () => {
      clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [targetPercent, durationMs, delayMs]);
  return value;
}

function ProgressBar({ percent, minLabel = 'Low', maxLabel = 'High', label, color = 'bg-white', trackColor = 'bg-white/30', heightClass = 'h-6', labelTextColor = '#111827', startAnimation = true }) {
  const animated = useAnimatedPercent(startAnimation ? percent : 0, 900, 80);
  const widthStyle = { width: `${Math.max(0, Math.min(100, animated)).toFixed(1)}%` };
  return (
    <div className="w-full">
      <div className={`relative w-full ${heightClass} rounded-full ${trackColor}`}> 
        <div className={`absolute inset-y-0 left-0 rounded-full ${color} flex items-center overflow-hidden`} style={widthStyle}>
          {label && (
            <span className="ml-2 inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold shadow-sm whitespace-nowrap" style={{ color: labelTextColor }}>
              {label}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex w-full justify-between text-xs text-white/90">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

function DoubleProgressGroup({ bars = [], minLabel = 'Low', maxLabel = 'High', startAnimation = true }) {
  return (
    <div className="w-full">
      <div className="space-y-3">
        {bars.map((b, idx) => (
          <div key={idx} className="w-full">
            <ProgressBar percent={b.percent} label={b.label} minLabel="" maxLabel="" color={b.color} trackColor={b.trackColor} labelTextColor={b.labelTextColor || '#0e7490'} startAnimation={startAnimation} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex w-full justify-between text-xs text-white/90">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

// Gradient color configurations for each card (hex colors)
const CARD_GRADIENTS = {
  candidateScore: {
    from: '#41537C',
    to: '#6387DB',
  },
  expectedCost: {
    from: '#277D48', 
    to: '#37AC64',   
  },
  expectedSatisfaction: {
    from: '#AC3FBC', 
    to: '#E570F7',  
  },
  medicalBackground: {
    from: '#A6502B',
    to: '#ED956F',   
  },
  aestheticGoals: {
    from: '#208EA1',
    to: '#5FD9DF',   
  },
};

function ColoredCard({ title, subtitle, gradientColors, children }) {
  return (
    <div 
      className="rounded-2xl p-4 sm:p-5 shadow-lg text-white"
      style={{
        background: `linear-gradient(to bottom right, ${gradientColors.from}, ${gradientColors.to})`
      }}
    > 
      <div className="mb-3">
        <h3 className="text-base sm:text-lg font-semibold leading-snug">{title}</h3>
        {subtitle && (
          <p className="mt-1 text-xs sm:text-sm text-white/90">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

export default function ConversionSlide({
  title = 'Your personalized summary',
  subtitle = '',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',
  showFooterSubtext = false,
  footerSubtextText = '',
  footerSubtextProps = {},
  // Dynamic values from controller
  candidateScorePercent = 72,
  expectedCostValue = 4300,
  expectedCostLabel = '$4200-$4300',
  expectedSatisfactionPercent = 92,
  medicalHairLossPercent = 85,
  medicalStressPercent = 65,
  medicalSocialImpactPercent = 55,
  aestheticGoalsPercent = 75,
  // Session ID for loading images from artifacts
  sessionId = null,
}) {
  // Load before/after images from artifacts
  // Read artifacts fresh on every render to ensure we always have the latest data
  // This fixes issues where artifacts might not be populated when the component first mounts
  // or when the user navigates back and forth
  const artifact = sessionId ? getImageUploadArtifact(sessionId) : null;
  const beforeImage = artifact?.imageDataUrl || null;
  const afterImage = artifact?.hairgenResultUrl || null;
  const hasComparisonImages = !!(beforeImage && afterImage);

  // Card data with render functions to support delayed bar animations
  const cards = useMemo(() => ([
    {
      key: 'candidate-score',
      gradientColors: CARD_GRADIENTS.candidateScore,
      title: 'Hair Transplant Candidate Score',
      subtitle: 'How suitable are you for a hair transplant',
      renderContent: (startAnimation) => (
        <ProgressBar percent={candidateScorePercent} label={`${candidateScorePercent}%`} minLabel="Low" maxLabel="High" color="bg-white" trackColor="bg-white/30" labelTextColor={CARD_GRADIENTS.candidateScore.from} startAnimation={startAnimation} />
      ),
    },
    {
      key: 'expected-cost',
      gradientColors: CARD_GRADIENTS.expectedCost,
      title: 'Expected Cost of Surgery',
      subtitle: 'Based on 1000+ reviews matched to your attributes',
      renderContent: (startAnimation) => {
        const min = -2000; const max = 10000;
        const pct = Math.max(0, Math.min(100, ((expectedCostValue - min) / (max - min)) * 100));
        return <ProgressBar percent={pct} label={expectedCostLabel} minLabel="Low" maxLabel="High" color="bg-white" trackColor="bg-white/30" labelTextColor={CARD_GRADIENTS.expectedCost.from} startAnimation={startAnimation} />;
      },
    },
    {
      key: 'expected-satisfaction',
      gradientColors: CARD_GRADIENTS.expectedSatisfaction,
      title: 'Expected Satisfaction',
      subtitle: 'Based on 1000+ reviews matched to your attributes',
      renderContent: (startAnimation) => (
        <ProgressBar percent={expectedSatisfactionPercent} label={`${expectedSatisfactionPercent}%`} minLabel="Low" maxLabel="High" color="bg-white" trackColor="bg-white/30" labelTextColor={CARD_GRADIENTS.expectedSatisfaction.from} startAnimation={startAnimation} />
      ),
    },
    {
      key: 'medical-background',
      gradientColors: CARD_GRADIENTS.medicalBackground,
      title: 'Personal Background',
      renderContent: (startAnimation) => {
        const hairMin = -60; const hairMax = 100;
        const hairPct = Math.max(0, Math.min(100, ((medicalHairLossPercent - hairMin) / (hairMax - hairMin)) * 100));
        return (
          <DoubleProgressGroup
            bars={[
              { percent: hairPct, label: 'Hair loss level', color: 'bg-white', trackColor: 'bg-white/30', labelTextColor: CARD_GRADIENTS.medicalBackground.from },
              { percent: medicalStressPercent, label: 'Stress level', color: 'bg-white', trackColor: 'bg-white/30', labelTextColor: CARD_GRADIENTS.medicalBackground.from },
              { percent: medicalSocialImpactPercent, label: 'Social effect', color: 'bg-white', trackColor: 'bg-white/30', labelTextColor: CARD_GRADIENTS.medicalBackground.from },
            ]}
            minLabel="Low"
            maxLabel="High"
            startAnimation={startAnimation}
          />
        );
      },
    },
    {
      key: 'aesthetic-goals',
      gradientColors: CARD_GRADIENTS.aestheticGoals,
      title: 'Aesthetic Goals',
      subtitle: 'How realistic are your goals based on our data',
      renderContent: (startAnimation) => (
        <ProgressBar percent={aestheticGoalsPercent} label={`${aestheticGoalsPercent}%`} minLabel="Unrealistic" maxLabel="Realistic" color="bg-white" trackColor="bg-white/30" labelTextColor={CARD_GRADIENTS.aestheticGoals.from} startAnimation={startAnimation} />
      ),
    },
  ]), [candidateScorePercent, expectedCostValue, expectedCostLabel, expectedSatisfactionPercent, medicalHairLossPercent, medicalStressPercent, medicalSocialImpactPercent, aestheticGoalsPercent]);

  // Three-group sequential appearance system:
  // Group 1: ResultTimeDisplay
  // Group 1.5: Image Comparison (new)
  // Group 2: Colored Cards + BenefitsDisplay
  
  const [resultTimeComplete, setResultTimeComplete] = useState(false);
  
  // Group 1.5: Image Comparison state
  const [showImageComparisonHeader, setShowImageComparisonHeader] = useState(false);
  const [imageComparisonHeaderComplete, setImageComparisonHeaderComplete] = useState(false);
  const [showImageComparisonContent, setShowImageComparisonContent] = useState(false);
  const [imageComparisonComplete, setImageComparisonComplete] = useState(false);
  
  // Group 2: Cards state
  const [visibleCardCount, setVisibleCardCount] = useState(0);
  const [showBenefits, setShowBenefits] = useState(false);
  const [miniHeaderComplete, setMiniHeaderComplete] = useState(false);

  // Primary button visibility - only show after "Your snapshot analysis" header appears
  const [showNextButton, setShowNextButton] = useState(false);

  // When ResultTime completes, start showing Group 1.5 (if we have images) or skip to Group 2
  useEffect(() => {
    if (!resultTimeComplete) return;
    
    if (hasComparisonImages) {
      // Show image comparison header first
      setShowImageComparisonHeader(true);
    } else {
      // Skip to Group 2 if no images available
      setImageComparisonComplete(true);
    }
  }, [resultTimeComplete, hasComparisonImages]);

  // After image comparison header animates in, show the image content
  useEffect(() => {
    if (!imageComparisonHeaderComplete) return;
    setShowImageComparisonContent(true);
  }, [imageComparisonHeaderComplete]);

  // After image comparison content animates in, mark group complete and move to Group 2
  const handleImageComparisonContentAnimEnd = () => {
    // Small delay before moving to next group for better UX
    setTimeout(() => {
      setImageComparisonComplete(true);
    }, 300);
  };

  // After Group 1.5 completes, start Group 2 (mini header)
  useEffect(() => {
    if (!imageComparisonComplete) return;
    // miniHeaderComplete is triggered by the header's onAnimationEnd
  }, [imageComparisonComplete]);

  // After mini header completes, start card reveals and show the primary button
  useEffect(() => {
    if (!miniHeaderComplete) return;
    setVisibleCardCount(1); // Show first card after header
    setShowNextButton(true); // Enable primary button
  }, [miniHeaderComplete]);

  // Skip handler: advance through all group animations one by one (only after ResultTime completes)
  const handleSkipAdvance = () => {
    // Skip is disabled until ResultTime completes
    if (!resultTimeComplete) {
      return;
    }
    
    // Group 1.5: Image Comparison (if available)
    if (hasComparisonImages && !imageComparisonComplete) {
      if (!showImageComparisonHeader) {
        setShowImageComparisonHeader(true);
        return;
      }
      if (!imageComparisonHeaderComplete) {
        setImageComparisonHeaderComplete(true);
        return;
      }
      if (!showImageComparisonContent) {
        setShowImageComparisonContent(true);
        return;
      }
      // Skip to complete
      setImageComparisonComplete(true);
      return;
    }
    
    // Group 2: Cards
    if (!miniHeaderComplete) {
      // Skip to header complete
      setMiniHeaderComplete(true);
      return;
    }
    
    // Advance cards one by one
    if (visibleCardCount < cards.length) {
      setVisibleCardCount(visibleCardCount + 1);
    } else if (!showBenefits) {
      // After all cards, show BenefitsDisplay
      setShowBenefits(true);
    }
  };

  const touchHandlers = resultTimeComplete ? {
    onPointerDown: handleSkipAdvance,
    onClick: handleSkipAdvance,
    onTouchStart: handleSkipAdvance,
  } : {};

  return (
    <div {...touchHandlers}>
    <SlideShell
      // Keep title/subtitle inside scroll flow, but prevent them from sliding in with cards
      title={null}
      subtitle={null}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onBack={onBack}
      nextLabel="Get your hair back"
      theme={theme}
      animationDirection={animationDirection}
      isTransitioning={isTransitioning}
      showFooterSubtext={showFooterSubtext}
      footerSubtextText={footerSubtextText}
      footerSubtextProps={footerSubtextProps}
      showPrimary={showNextButton}
      scrollable
      contentJustify="start"
      contentClassName="no-scrollbar-desktop"
      animateScrollableChildrenOnly
      hideBottomSpacer={true}
      topScrollContent={(
        (title || subtitle) && (
          <div className="mx-auto w-full max-w-md text-white mb-4">
            {title && (
              <h1 className="title-fade text-2xl font-bold leading-tight pb-2">{title}</h1>
            )}
            {subtitle && (
              <p className="title-fade text-sm text-gray-300 leading-relaxed">{subtitle}</p>
            )}
          </div>
        )
      )}
    >
      <style>{`
        @keyframes cardFadeIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .card-reveal {
          opacity: 0;
          animation: cardFadeIn 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .card-reveal { animation: cardFadeIn 1ms linear forwards !important; }
        }
        .no-scrollbar-desktop::-webkit-scrollbar { display: none; }
        .no-scrollbar-desktop { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      <div className="mx-auto w-full max-w-md mt-12">
        {/* Group 1: ResultTimeDisplay */}
        <ResultTimeDisplay 
          monthsFromNow={9} 
          onComplete={() => setResultTimeComplete(true)}
        />
        
        {/* Group 1.5: Image Comparison - appears after ResultTime completes (if images available) */}
        {resultTimeComplete && hasComparisonImages && (
          <div className="mt-12">
            {/* Header for image comparison */}
            {showImageComparisonHeader && (
              <div 
                className="card-reveal mb-4"
                onAnimationEnd={() => setImageComparisonHeaderComplete(true)}
              >
                <h2 className="text-white text-xl font-bold leading-tight mb-1">
                  Simulated Results
                </h2>
                <p className="text-sm text-gray-300 leading-relaxed">
                  This is how our algorithm predicts your results may look like with the right clinic.
                </p>
              </div>
            )}

            {/* Image comparison slider */}
            {showImageComparisonContent && (
              <div 
                className="card-reveal"
                onAnimationEnd={handleImageComparisonContentAnimEnd}
              >
                <BeforeAfterSlider
                  beforeImage={beforeImage}
                  afterImage={afterImage}
                  beforeLabel="Before"
                  afterLabel="After"
                  initialPosition={50}
                  className="shadow-lg"
                />
                <p className="mt-3 text-xs text-gray-400 text-center leading-relaxed">
                  This is not a medical diagnosis. Match Medic uses advanced algorithms. Results may vary.
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Group 2: Colored Cards - appear after Group 1.5 completes (or after ResultTime if no images) */}
        {imageComparisonComplete && (
          <div className="space-y-4 mt-12">
            {/* Mini title/subtitle header for cards group */}
            <div 
              className="card-reveal mb-4"
              onAnimationEnd={() => setMiniHeaderComplete(true)}
            >
              <h2 className="text-white text-xl font-bold leading-tight mb-1">
                Your snapshot analysis
              </h2>
              <p className="text-sm text-gray-300 leading-relaxed">
                Here's what we learned about you so far.
              </p>
            </div>

            {cards.slice(0, visibleCardCount).map((c, idx) => {
              const isLastVisible = idx === visibleCardCount - 1;
              const shouldAnimateBar = true; // Bar animates after card fade-in
              
              return (
                <div
                  key={c.key}
                  className="card-reveal"
                  onAnimationEnd={() => {
                    // Chain to next card after current card's fade-in completes
                    if (isLastVisible && visibleCardCount < cards.length) {
                      setVisibleCardCount(visibleCardCount + 1);
                    } else if (isLastVisible && visibleCardCount === cards.length && !showBenefits) {
                      // After last card, show BenefitsDisplay
                      setShowBenefits(true);
                    }
                  }}
                >
                  <ColoredCard title={c.title} subtitle={c.subtitle} gradientColors={c.gradientColors}>
                    {/* Render content with startAnimation prop for bars */}
                    {c.renderContent(shouldAnimateBar)}
                  </ColoredCard>
                </div>
              );
            })}

            {/* BenefitsDisplay - appears after all cards */}
            {showBenefits && (
              <div className="card-reveal">
                <BenefitsDisplay />
              </div>
            )}
          </div>
        )}
      </div>
    </SlideShell>
    </div>
  );
}